import type { ReactNode } from 'react';
import { Check, SlidersHorizontal } from 'lucide-react';

/**
 * Shared filter UI for the marketplace browse screens (Coaches, Clubs).
 *
 * Design rationale:
 *   - Each row gets a small uppercase label so the dimension is named
 *     instead of being inferred from the first pill's text.
 *   - Pills have three visual states, not two:
 *       1. Active filter (selected + not the default value) → brand
 *          green with a check, so engaged filters pop out across many
 *          rows.
 *       2. Default selected ("Any X") → neutral filled, signalling
 *          "no filter applied here" without looking unselected.
 *       3. Unselected → outline-only, low-contrast text — recedes so
 *          the eye lands on the engaged filters.
 *   - A "Filters · N applied" header with a "Clear all" affordance
 *     appears only when at least one row is non-default, so a fresh
 *     view stays quiet.
 */

export interface FilterPillRowProps<T extends string> {
  /** Short label naming the dimension, e.g. "Lesson type". */
  label: string;
  /** Value treated as "no filter applied" (typically 'any'). */
  defaultValue: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
}

export function FilterPillRow<T extends string>({
  label, defaultValue, options, value, onChange,
}: FilterPillRowProps<T>) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground mb-1.5 px-1 font-medium">
        {label}
      </div>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
        {options.map((o) => {
          const sel = o.value === value;
          const isActive = sel && o.value !== defaultValue;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              aria-pressed={sel}
              className={`shrink-0 h-9 px-3.5 rounded-full text-xs whitespace-nowrap inline-flex items-center gap-1.5 transition-all border ${
                isActive
                  ? 'bg-[#1A6B4F] text-white border-[#1A6B4F] shadow-sm'
                  : sel
                    ? 'bg-secondary text-foreground border-secondary'
                    : 'bg-card text-foreground/60 border-border hover:border-[#1A6B4F]/40 hover:text-foreground'
              }`}
            >
              {isActive && <Check className="w-3 h-3" strokeWidth={3} />}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FilterPanel({
  activeCount, onClearAll, children,
}: {
  activeCount: number;
  onClearAll: () => void;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-medium">
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#1A6B4F] text-white text-[10px] px-1 normal-case tracking-normal font-semibold">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="text-[11px] text-[#1A6B4F] font-medium hover:underline"
          >
            Clear all
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
